import { useContext } from 'react'
import { NavLink } from 'react-router-dom'
import { ECE } from '@BUP/constants'
import { isModeBup, isSociogram, isData, hasLogbookEnabled } from '@BUP/services/BraveUp'
import { useTranslation } from 'react-i18next'
import styles from './generic-navigation.module.scss'
import classNames from 'classnames'
import IconMenu from '@BUP/components/IconMenu'
import Rbac from '@BUP/components/RBAC'
import { RoleContext } from '@BUP/contexts/RoleContext'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import HistoricSociogramNavLink from './historic-sociogram-nav-link'
import { BookOutlined } from '@ant-design/icons'

const GenericNavigation = () => {
  const [t] = useTranslation()
  const role = useContext(RoleContext)
  const organization = useContext(OrganizationContext)
  const roleType = role.type
  const organizationCountry = organization.country
  const getNavLinkClassName = ({ isActive }) =>
    classNames(styles.nav_link, { [styles.active]: isActive, active: isActive })
  const getSubNavLinkClassName = ({ isActive }) =>
    classNames(styles.sub_nav_link, { [styles.active]: isActive, active: isActive })

  return (
    <nav className={styles.navigation_wrapper}>
      <div className={styles.links_wrapper}>
        <div className={styles.element_wrapper}>
          <NavLink
            to='/'
            end
            className={getNavLinkClassName}
          >
            <IconMenu iconName='home' />
            <span className={styles.nav_label}>{t('nav.home')}</span>
            <span className={styles.nav_label_mobile}>
              {t('nav.homeMobile')}
            </span>
          </NavLink>
        </div>
        {roleType === ECE
          ? (
            <div
              className={styles.element_wrapper + ' ' + styles.element_removable}
            >
              <NavLink
                to='/schoolpage'
                className={getNavLinkClassName}
              >
                <IconMenu iconName='school' />
                <span className={styles.nav_label}>{t('nav.mySchool')}</span>
              </NavLink>
            </div>
            )
          : null}
        <div className={styles.element_wrapper}>
          <NavLink
            to='/resources'
            className={getNavLinkClassName}
          >
            <IconMenu iconName='resources' />
            <span className={styles.nav_label}>{t('nav.resources')}</span>
            <span className={styles.nav_label_mobile}>
              {t('nav.resourcesMobile')}
            </span>
          </NavLink>
        </div>
        {isModeBup(organization)
          ? (
            <>
              <div
                className={
                styles.element_wrapper + ' ' + styles.element_removable
              }
              >
                <NavLink
                  to='/modebup'
                  className={getNavLinkClassName}
                >
                  <IconMenu iconName='modoBUP' />
                  <span className={styles.nav_label}>{t('nav.bupmode')}</span>
                  <span className={styles.nav_label_mobile}>
                    {t('nav.bupmodeMobile')}
                  </span>
                </NavLink>
                <Rbac name='navModeBupSendLink'>
                  <NavLink
                    to='/modebup/send'
                    className={getSubNavLinkClassName}
                  >
                    <span className={styles.nav_label}>
                      {t('nav.sendMessage')}
                    </span>
                  </NavLink>
                </Rbac>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <NavLink
                    to='/modebup/inbox'
                    className={getSubNavLinkClassName}
                  >
                    <span className={styles.nav_label}>{t('nav.inbox')}</span>
                  </NavLink>
                </div>
                <Rbac name='navModeBupArchivedLink'>
                  <NavLink
                    to='/modebup/archived'
                    className={getSubNavLinkClassName}
                  >
                    <span className={styles.nav_label}>
                      {t('nav.archivedCases')}
                    </span>
                  </NavLink>
                </Rbac>
              </div>

              <div
                className={
                styles.element_wrapper + ' ' + styles.element_mobile_only
              }
              >
                <NavLink
                  to='/modebup'
                  end
                  className={getNavLinkClassName}
                >
                  <IconMenu iconName='modoBUP' />
                  <span className={styles.nav_label_mobile}>
                    {t('nav.bupmodeMobile')}
                  </span>
                  <span className={styles.nav_label}>{t('nav.bupmode')}</span>
                </NavLink>
              </div>

              <div
                className={
                styles.element_wrapper + ' ' + styles.element_mobile_only
              }
              >
                <NavLink
                  to='/modebup/inbox'
                  className={getNavLinkClassName}
                >
                  <IconMenu iconName='inbox' />
                  <span className={styles.nav_label}>{t('nav.inbox')}</span>
                  <span className={styles.nav_label_mobile}>
                    {t('nav.inboxMobile')}
                  </span>
                </NavLink>
              </div>
            </>
            )
          : null}
        <div className={styles.element_wrapper}>
          {organizationCountry === 'Spain'
            ? null
            : (
              <>
                <NavLink
                  to='/data'
                  className={getNavLinkClassName}
                >
                  <IconMenu iconName='data' />
                  <span className={styles.nav_label}>{t('nav.data')}</span>
                  <span className={styles.nav_label_mobile}>
                    {t('nav.dataMobile')}
                  </span>
                </NavLink>
              </>
              )}
          {isData(organization) && organizationCountry !== 'Spain' && (
            <Rbac name='navDiagnostic'>
              <NavLink
                to='/data/diagnostic'
                className={getSubNavLinkClassName}
              >
                <span className={styles.nav_label}>{t('nav.diagnostic')}</span>
              </NavLink>
            </Rbac>
          )}
          {isSociogram(organization) &&
          organizationCountry !== 'Spain' &&
          roleType === ECE
            ? (
              <Rbac name='navSociogram'>
                <NavLink
                  to='/sociogram'
                  data-test='nav-sociogram-ece'
                  className={getSubNavLinkClassName}
                >
                  <span className={styles.nav_label}>{t('nav.sociogram')}</span>
                </NavLink>
              </Rbac>
              )
            : null}
          <HistoricSociogramNavLink isSociogram={isSociogram(organization)} roleType={roleType} />
        </div>
        {hasLogbookEnabled(organization) && [ECE].includes(roleType) && (
          <div className={styles.element_wrapper}>
            <NavLink
              to='/logbook'
              className={getNavLinkClassName}
            >
              <BookOutlined className={classNames('menu_icon', styles.nav_icon)} />{/* TODO: sprites icon for <IconMenu iconName='logbook' /> */}
              <span className={styles.nav_label}>{t('nav.logbook')}</span>
              <span className={styles.nav_label_mobile}>
                {t('nav.logbook')}
              </span>
            </NavLink>
          </div>
        )}
      </div>
    </nav>
  )
}

export default GenericNavigation
